/* Albindress — global.js
   Utilità condivise, drawer accessibili, menu, carosello, quantità, annunci. */
(function () {
  'use strict';

  const SG = (window.Albin = window.Albin || {});

  /* ---------- Utilità ---------- */
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"]), iframe, object, embed, [contenteditable]';

  SG.getFocusable = function (container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
      if (el.closest('[hidden]') || el.closest('[inert]')) return false;
      // Esclude gli elementi dentro <details> chiusi (tranne il summary)
      const details = el.parentElement && el.parentElement.closest('details:not([open])');
      if (details && el.tagName !== 'SUMMARY') {
        const summary = details.querySelector(':scope > summary');
        if (summary !== el) return false;
      }
      return el.getClientRects().length > 0;
    });
  };

  let activeTrap = null;
  SG.trapFocus = function (container, initialFocus) {
    SG.releaseFocus();
    const handler = function (event) {
      if (event.key !== 'Tab') return;
      const items = SG.getFocusable(container);
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    activeTrap = handler;
    const target = initialFocus || SG.getFocusable(container)[0] || container;
    target.focus({ preventScroll: true });
  };

  SG.releaseFocus = function () {
    if (activeTrap) document.removeEventListener('keydown', activeTrap);
    activeTrap = null;
  };

  SG.announce = function (message, assertive) {
    const region = document.getElementById(assertive ? 'AlertRegion' : 'LiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  };

  SG.debounce = function (fn, wait) {
    let t;
    return function () {
      const args = arguments;
      const ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  };

  SG.parseHTML = function (html) {
    return new DOMParser().parseFromString(html, 'text/html');
  };

  SG.prefersReducedMotion = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ---------- Drawer generico ---------- */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) {
          event.stopPropagation();
          this.close();
        }
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      if (this.trigger && this.trigger.setAttribute && this.trigger.hasAttribute('aria-expanded')) {
        this.trigger.setAttribute('aria-expanded', 'true');
      }
      const initial = this.panel.querySelector('[data-drawer-initial-focus]');
      window.requestAnimationFrame(() => SG.trapFocus(this.panel, initial || this.panel));
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      SG.releaseFocus();
      if (this.trigger && this.trigger.setAttribute && this.trigger.hasAttribute('aria-expanded')) {
        this.trigger.setAttribute('aria-expanded', 'false');
      }
      if (this.trigger && document.contains(this.trigger)) {
        this.trigger.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  SG.DrawerElement = DrawerElement;

  if (!customElements.get('menu-drawer')) {
    customElements.define(
      'menu-drawer',
      class MenuDrawer extends DrawerElement {
        connectedCallback() {
          super.connectedCallback();
          // Chiude il menu passando a desktop
          const mq = window.matchMedia('(min-width: 990px)');
          const onChange = () => {
            if (mq.matches) this.close();
          };
          if (mq.addEventListener) mq.addEventListener('change', onChange);
        }
      }
    );
  }

  /* Pannello laterale generico (es. guida alle taglie) */
  if (!customElements.get('side-drawer')) {
    customElements.define('side-drawer', class SideDrawer extends DrawerElement {});
  }

  /* Ricerca a tutta larghezza dall'alto */
  if (!customElements.get('search-drawer')) {
    customElements.define(
      'search-drawer',
      class SearchDrawer extends DrawerElement {
        open(trigger) {
          super.open(trigger);
          window.requestAnimationFrame(() =>
            window.requestAnimationFrame(() => {
              const input = this.querySelector('input[type="search"]');
              if (input) input.focus({ preventScroll: true });
            })
          );
        }
      }
    );
  }

  /* Header fisso che si nasconde scorrendo verso il basso e ricompare verso l'alto */
  (function () {
    const wrapper = document.querySelector('.section-site-header');
    const header = wrapper && wrapper.querySelector('[data-header-smart]');
    if (!header) return;
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = function () {
      const y = window.scrollY;
      wrapper.classList.toggle('is-scrolled', y > 8);
      const hide = y > lastY && y > 240 && !document.body.classList.contains('overflow-hidden') && !header.contains(document.activeElement);
      wrapper.classList.toggle('is-hidden', hide);
      lastY = y;
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(onScroll);
        }
      },
      { passive: true }
    );
    header.addEventListener('focusin', function () {
      wrapper.classList.remove('is-hidden');
    });
    onScroll();
  })();

  document.addEventListener('click', function (event) {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.getAttribute('data-drawer-open'));
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open(opener);
    }
  });

  /* ---------- Menu desktop / mega menu ---------- */
  if (!customElements.get('header-menu')) {
    customElements.define(
      'header-menu',
      class HeaderMenu extends HTMLElement {
        connectedCallback() {
          this.toggles = Array.from(this.querySelectorAll('[data-menu-toggle]'));
          this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
          this.toggles.forEach((toggle) => {
            const item = toggle.parentElement;
            toggle.addEventListener('click', () => {
              const expanded = toggle.getAttribute('aria-expanded') === 'true';
              if (expanded) {
                this.closePanel(toggle);
              } else {
                this.openPanel(toggle);
              }
            });
            if (this.hoverCapable) {
              let timer;
              item.addEventListener('mouseenter', () => {
                clearTimeout(timer);
                timer = setTimeout(() => this.openPanel(toggle), 120);
              });
              item.addEventListener('mouseleave', () => {
                clearTimeout(timer);
                timer = setTimeout(() => this.closePanel(toggle), 180);
              });
            }
            item.addEventListener('focusout', (event) => {
              if (!item.contains(event.relatedTarget)) this.closePanel(toggle);
            });
            item.addEventListener('keydown', (event) => {
              if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
                event.stopPropagation();
                this.closePanel(toggle);
                toggle.focus();
              }
            });
          });
          document.addEventListener('click', (event) => {
            if (!this.contains(event.target)) this.closeAll();
          });
        }

        openPanel(toggle) {
          this.toggles.forEach((other) => {
            if (other !== toggle) this.closePanel(other);
          });
          const panel = document.getElementById(toggle.getAttribute('aria-controls'));
          if (!panel) return;
          panel.hidden = false;
          toggle.setAttribute('aria-expanded', 'true');
        }

        closePanel(toggle) {
          const panel = document.getElementById(toggle.getAttribute('aria-controls'));
          if (panel) panel.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
        }

        closeAll() {
          this.toggles.forEach((toggle) => this.closePanel(toggle));
        }
      }
    );
  }

  /* ---------- Barra annunci ---------- */
  if (!customElements.get('announcement-bar')) {
    customElements.define(
      'announcement-bar',
      class AnnouncementBar extends HTMLElement {
        connectedCallback() {
          this.items = Array.from(this.querySelectorAll('.announcement-bar__item'));
          if (this.items.length < 2) return;
          this.index = 0;
          const prev = this.querySelector('[data-announcement-prev]');
          const next = this.querySelector('[data-announcement-next]');
          if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
          if (next) next.addEventListener('click', () => this.go(this.index + 1));

          const delay = parseInt(this.dataset.autoplay, 10);
          this.pauseButton = this.querySelector('[data-announcement-pause]');
          if (delay && !SG.prefersReducedMotion()) {
            this.delay = delay;
            this.userPaused = false;
            this.play();
            this.addEventListener('mouseenter', () => this.stop());
            this.addEventListener('mouseleave', () => this.play());
            this.addEventListener('focusin', () => this.stop());
            this.addEventListener('focusout', () => this.play());
            if (this.pauseButton) {
              this.pauseButton.addEventListener('click', () => {
                this.userPaused = !this.userPaused;
                this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
                if (this.userPaused) this.stop();
              });
            }
          } else if (this.pauseButton) {
            this.pauseButton.hidden = true;
          }
        }

        go(index) {
          const count = this.items.length;
          this.index = (index + count) % count;
          this.items.forEach((item, i) => {
            item.hidden = i !== this.index;
          });
        }

        play() {
          if (this.userPaused || !this.delay) return;
          this.stop();
          this.timer = setInterval(() => this.go(this.index + 1), this.delay);
        }

        stop() {
          clearInterval(this.timer);
        }
      }
    );
  }

  /* ---------- Carosello (scroll-snap + pulsanti) ---------- */
  if (!customElements.get('slider-component')) {
    customElements.define(
      'slider-component',
      class SliderComponent extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-slider-track]');
          this.prev = this.querySelector('[data-slider-prev]');
          this.next = this.querySelector('[data-slider-next]');
          if (!this.track || !this.prev || !this.next) return;
          this.prev.addEventListener('click', () => this.move(-1));
          this.next.addEventListener('click', () => this.move(1));
          this.track.addEventListener('scroll', SG.debounce(() => this.update(), 60), { passive: true });
          window.addEventListener('resize', SG.debounce(() => this.update(), 150));
          this.update();
        }

        move(direction) {
          const item = this.track.firstElementChild;
          if (!item) return;
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          const step = item.getBoundingClientRect().width + gap;
          const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
          this.track.scrollBy({
            left: direction * step * visible,
            behavior: SG.prefersReducedMotion() ? 'auto' : 'smooth',
          });
        }

        update() {
          const max = this.track.scrollWidth - this.track.clientWidth - 2;
          const scrollable = max > 2;
          const controls = this.querySelector('.slider__controls');
          if (controls) controls.hidden = !scrollable;
          this.prev.disabled = this.track.scrollLeft <= 2;
          this.next.disabled = this.track.scrollLeft >= max;
        }
      }
    );
  }

  /* ---------- Selettore quantità ---------- */
  if (!customElements.get('quantity-input')) {
    customElements.define(
      'quantity-input',
      class QuantityInput extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('input');
          this.addEventListener('click', (event) => {
            const button = event.target.closest('button[name]');
            if (!button || !this.input) return;
            event.preventDefault();
            const step = parseInt(this.input.step, 10) || 1;
            const min = parseInt(this.input.min, 10) || 0;
            const max = this.input.max ? parseInt(this.input.max, 10) : null;
            let value = parseInt(this.input.value, 10) || min;
            value = button.name === 'plus' ? value + step : value - step;
            if (value < min) value = min;
            if (max !== null && value > max) value = max;
            if (String(value) === this.input.value) return;
            this.input.value = value;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
            this.updateButtons();
          });
          this.input && this.input.addEventListener('change', () => this.updateButtons());
          this.updateButtons();
        }

        updateButtons() {
          if (!this.input) return;
          const value = parseInt(this.input.value, 10);
          const min = parseInt(this.input.min, 10) || 0;
          const max = this.input.max ? parseInt(this.input.max, 10) : null;
          const minus = this.querySelector('button[name="minus"]');
          const plus = this.querySelector('button[name="plus"]');
          if (minus) minus.disabled = value <= min;
          if (plus) plus.disabled = max !== null && value >= max;
        }
      }
    );
  }

  /* ---------- Disclosure apribili solo su desktop (filtri) ---------- */
  function syncDesktopDetails() {
    const mq = window.matchMedia('(min-width: 990px)');
    const apply = () => {
      document.querySelectorAll('details[data-open-desktop]').forEach((el) => {
        if (mq.matches) el.setAttribute('open', '');
      });
    };
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncDesktopDetails);
  } else {
    syncDesktopDetails();
  }
})();
